import { createContext, useContext, type ReactNode } from "react";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import type { TenantRole } from "../auth/usePortalIdentity";

/**
 * Where the client's screens read from. Build round 41, 2026-10-11, "Visa som kund".
 *
 * `client` is the ordinary portal: every hook reads the client's own views and gateways, under the
 * session's own membership. `admin` is Petrovic, in admin, looking at one client's portal as that
 * client sees it. Then each hook reads the matching `public.admin_as_client_*` gateway for that one
 * client, which answers exactly the rows and columns the client's own read answers
 * (`tests/db/round41-view-as-client.test.ts`), and never a session as the client.
 *
 * The view is read-only, and that is `viewOnly`, never `canWrite`: a client whose portal is paused
 * sees its own "låst" sentences, and the view shows those as they are. `viewOnly` instead removes
 * every control that writes, and the mutation hooks refuse before any request leaves.
 */
export type ReadSource =
  | { kind: "client" }
  | {
      kind: "admin";
      tenantId: string;
      tenantName: string;
      role: TenantRole;
      /** The person the banner names: an active member with that role, or null when there is none. */
      personName: string | null;
      /** `/admin/clients/<id>/as-client`, which every client path in the view is put under. */
      base: string;
    };

const CLIENT: ReadSource = { kind: "client" };

const ReadSourceContext = createContext<ReadSource>(CLIENT);

export function ReadSourceProvider({ source, children }: { source: ReadSource; children: ReactNode }) {
  return <ReadSourceContext.Provider value={source}>{children}</ReadSourceContext.Provider>;
}

export function useReadSource(): ReadSource {
  return useContext(ReadSourceContext);
}

/** True inside "Visa som kund": nothing that writes renders. */
export function useViewOnly(): boolean {
  return useReadSource().kind === "admin";
}

/**
 * A client path as this screen should link it: `/requests/12` in the portal, and
 * `/admin/clients/<id>/as-client/requests/12` inside the view, so a link never leaves the view for
 * the client's own address (where Petrovic, who holds no membership, has no portal).
 */
export function useClientPath(): (path: string) => string {
  const source = useReadSource();
  return (path: string) =>
    source.kind === "admin" && path.startsWith("/") && !PUBLIC_PATHS.some((open) => path === open || path.startsWith(`${open}/`))
      ? `${source.base}${path}`
      : path;
}

/** Pages that belong to no client and are the same for everyone: linked as they are. */
const PUBLIC_PATHS = ["/privacy"];

/**
 * The query key a hook uses, kept apart from the client's own: the view's cache is
 * `["as-client", tenant, role, ...]`, so a read Petrovic made as a client never answers the portal's
 * own query, and the reverse.
 */
export function sourcedKey<K extends readonly unknown[]>(source: ReadSource, key: K): readonly unknown[] {
  return source.kind === "admin" ? (["as-client", source.tenantId, source.role, ...key] as const) : key;
}

/** One `public.admin_as_client_*` read, its rows or a thrown error, never an empty list for a failure. */
export async function viewRows<T>(gateway: string, args: Record<string, unknown>): Promise<T[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc(gateway as never, args as never);
  if (error) {
    throw error;
  }
  return ((data ?? []) as unknown as T[]);
}

/** The same for a gateway that answers one jsonb value, as the client's facts reads do. */
export async function viewValue<T>(gateway: string, args: Record<string, unknown>): Promise<T> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc(gateway as never, args as never);
  if (error) {
    throw error;
  }
  return data as unknown as T;
}

/**
 * The second lock behind the absent controls: a mutation reached inside the view throws before any
 * request leaves. The server is the third, since every client write gateway refuses Petrovic, who
 * holds no membership.
 */
export function refuseInView(source: ReadSource): void {
  if (source.kind === "admin") {
    throw new Error("PORTAL_VIEW_ONLY: Inget går att ändra här.");
  }
}
